import React from "react";
import Chapter from "@/components/landing/Chapter";
import Reveal from "@/components/landing/Reveal";
import PhotoFrame from "@/components/landing/PhotoFrame";
import { IMAGES } from "@/lib/landingConfig";
import { useLandingImages } from "@/lib/ImagesContext";

/**
 * Location — introduces the physical venue of the edition (Radisson
 * Collection Hotel, Sevilla) with an editorial split layout, a photo slot
 * that can be updated from /admin ("venue"), a Google Maps embed and
 * two contextual CTAs.
 */

const MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Radisson+Collection+Hotel+Magdalena+Plaza+Sevilla";
const MAPS_EMBED_SRC =
  "https://www.google.com/maps?q=Radisson+Collection+Hotel+Magdalena+Plaza+Sevilla&hl=es&z=16&output=embed";
const HOTEL_URL =
  "https://www.radissonhotels.com/es-es/hoteles/radisson-collection-sevilla";

const Location = () => {
  const { bySlot } = useLandingImages();
  const venueImg = bySlot["venue"] || IMAGES.venuePhoto;

  return (
    <section
      id="ubicacion"
      data-testid="location-section"
      className="bg-[#F4EFE7]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 lg:py-40">
        <div className="grid grid-cols-12 gap-10 md:gap-14 lg:gap-20 items-start">
          {/* Text column */}
          <div className="col-span-12 lg:col-span-6 lg:pt-4">
            <Reveal>
              <Chapter number="07" label="Ubicación" />
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#3F403A]">
                Un espacio a la altura
                <span className="italic text-[#63846F]"> del encuentro.</span>
              </h2>
            </Reveal>

            <Reveal delay={140}>
              <p className="mt-8 md:mt-10 font-sans text-base md:text-lg leading-relaxed text-[#3F403A]/85 max-w-xl">
                La Edición Primer Aniversario se celebrará en el{" "}
                <em className="not-italic font-medium text-[#3F403A]">
                  Radisson Collection Hotel, Sevilla
                </em>{" "}
                — un espacio contemporáneo, elegante y perfectamente pensado
                para acoger encuentros de alto valor.
              </p>
            </Reveal>

            <Reveal delay={220}>
              <p className="mt-4 font-sans text-base md:text-lg leading-relaxed text-[#3F403A]/80 max-w-xl">
                Un entorno cuidado que acompaña la conversación, el
                networking y la experiencia con la atmósfera que esta jornada
                merece.
              </p>
            </Reveal>

            <Reveal delay={280}>
              <div
                className="mt-10 md:mt-12 border-t border-[#3F403A]/12 pt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-xl"
                data-testid="location-details"
              >
                <div>
                  <p className="overline text-[#3F403A]/60">Sede oficial</p>
                  <p className="mt-2 font-serif text-xl md:text-2xl text-[#3F403A] leading-tight">
                    Radisson Collection Hotel
                  </p>
                  <p className="mt-1 font-sans text-sm text-[#3F403A]/70">
                    Sevilla, España
                  </p>
                </div>
                <div>
                  <p className="overline text-[#3F403A]/60">Fecha</p>
                  <p className="mt-2 font-serif text-xl md:text-2xl text-[#3F403A] leading-tight">
                    15 de octubre
                  </p>
                  <p className="mt-1 font-sans text-sm text-[#3F403A]/70">
                    Jornada presencial · 1 día
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={340}>
              <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 rounded-none border border-[#63846F] bg-[#63846F] text-[#FAF8F3] hover:bg-[#4E6D5A] hover:border-[#4E6D5A] transition-colors px-7 py-3.5 uppercase tracking-[0.22em] text-xs font-medium"
                  data-testid="location-cta-maps"
                >
                  Ver ubicación en el mapa <span aria-hidden>→</span>
                </a>
                <a
                  href={HOTEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 rounded-none border border-[#3F403A]/30 bg-transparent text-[#3F403A] hover:border-[#63846F] hover:text-[#63846F] transition-colors px-7 py-3.5 uppercase tracking-[0.22em] text-xs font-medium"
                  data-testid="location-cta-hotel"
                >
                  Sobre el hotel <span aria-hidden>→</span>
                </a>
              </div>
              <p className="mt-4 font-sans text-xs italic text-[#3F403A]/60 max-w-xl">
                La sala concreta y las indicaciones logísticas se comparten con
                las inscritas antes de la jornada.
              </p>
            </Reveal>
          </div>

          {/* Visual column */}
          <div className="col-span-12 lg:col-span-6">
            <Reveal>
              <PhotoFrame
                src={venueImg}
                alt="Radisson Collection Hotel Sevilla"
                ratio="4/5"
                label="Radisson Collection · Sevilla"
                testId="location-photo"
              />
            </Reveal>
            <Reveal delay={160}>
              <div
                className="mt-4 md:mt-6 relative overflow-hidden border border-[#3F403A]/10"
                data-testid="location-map"
                style={{ aspectRatio: "16/9" }}
              >
                <iframe
                  title="Ubicación · Radisson Collection Hotel Sevilla"
                  src={MAPS_EMBED_SRC}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="absolute inset-0 h-full w-full grayscale-[.15]"
                  style={{ border: 0 }}
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Location;
